"use client";

import { ResumeExample } from "@resume-studio/dto";
import { useCallback, useMemo, useState } from "react";

import { Button, TileCard } from "../";
export type ResumeSamplesProps = {
  resumeExamples: ResumeExample[];
  onTemplateSelected: (example: ResumeExample | null) => void;
};

export const ResumeSamples: React.FC<ResumeSamplesProps> = ({
  resumeExamples,
  onTemplateSelected,
}) => {
  const [activeFilter, setActiveFilter] = useState<string>("All");

  const hasAnyToken = useCallback((example: ResumeExample, tokens: string[]): boolean => {
    const normalize = (value: string | undefined | null): string => (value ?? "").toLowerCase();
    const title = normalize(example.title);
    const category = normalize(example.category);
    const description = normalize(example.description);
    const tags = (example.tags ?? []).map((t) => t.toLowerCase());
    return tokens.some((token) => {
      return (
        title.includes(token) ||
        category.includes(token) ||
        description.includes(token) ||
        tags.some((t) => t.includes(token))
      );
    });
  }, []);

  const curatedFilters = useMemo(
    () => [
      { label: "All", matches: (_ex: ResumeExample) => true },
      { label: "ATS", matches: (ex: ResumeExample) => hasAnyToken(ex, ["ats"]) },
      {
        label: "Simple",
        matches: (ex: ResumeExample) => hasAnyToken(ex, ["simple", "minimal", "classic", "basic"]),
      },
      {
        label: "Professional",
        matches: (ex: ResumeExample) =>
          hasAnyToken(ex, ["professional", "business", "corporate", "traditional"]),
      },
      {
        label: "Modern",
        matches: (ex: ResumeExample) =>
          hasAnyToken(ex, ["modern", "contemporary", "sleek", "clean"]),
      },
      {
        label: "Creative",
        matches: (ex: ResumeExample) =>
          hasAnyToken(ex, ["creative", "colorful", "designer", "visual"]),
      },
      {
        label: "Two-column",
        matches: (ex: ResumeExample) =>
          hasAnyToken(ex, ["two-column", "two column", "2-column", "2 column"]),
      },
    ],
    [hasAnyToken],
  );
  const filteredExamples = useMemo(() => {
    const filter = curatedFilters.find((f) => f.label === activeFilter);
    if (!filter) return resumeExamples;
    if (filter.label === "All") return resumeExamples;
    const matches = filter.matches;
    const results = resumeExamples.filter((ex) => matches(ex));
    if (results.length > 0) return results;
    // Fallback: attempt loose contains using the label
    const loose = activeFilter.toLowerCase();
    return resumeExamples.filter((ex) => hasAnyToken(ex, [loose]));
  }, [activeFilter, resumeExamples, curatedFilters, hasAnyToken]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-10">
        <div className="mx-auto mb-8 max-w-3xl text-center">
          <h1 className="text-4xl font-bold leading-tight">Resume templates</h1>
          <p className="mt-3 text-muted-foreground">
            Each resume template is designed to follow the exact rules you need to get hired faster.
            Use our resume templates and get free access to 18 more career tools!
          </p>
          <div className="mt-6">
            <Button className="px-6" onClick={() => onTemplateSelected(null)}>
              Create my resume
            </Button>
          </div>
        </div>

        <div className="mb-8 flex flex-wrap items-center justify-center gap-2">
          {curatedFilters.map(({ label }) => {
            const isActive = activeFilter === label;
            return (
              <button
                key={label}
                type="button"
                onClick={() => setActiveFilter(label)}
                className={
                  `rounded-full border px-3 py-1 text-sm transition-colors ` +
                  (isActive
                    ? "border-transparent bg-foreground text-background"
                    : "border-border bg-background text-foreground hover:bg-muted")
                }
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {filteredExamples.map((example) => (
            <div key={example.id}>
              <div
                className="group relative overflow-hidden rounded-xl ring-1 ring-black/5 transition-[transform,box-shadow] duration-200 hover:scale-[1.01] hover:shadow-[0_18px_32px_rgba(0,0,0,0.15)]"
                role="button"
                tabIndex={0}
                onClick={() => onTemplateSelected(example)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onTemplateSelected(example);
                }}
              >
                <TileCard
                  title={example.title}
                  hideMeta
                  imageUrl={example.thumbnailUrl}
                  backgroundSeed={example.id}
                />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-all duration-300 ease-out group-focus-within:opacity-100 group-hover:opacity-100">
                  <div className="pointer-events-auto rounded-xl shadow-2xl ring-1 ring-border/50 backdrop-blur-xl transition-all duration-300 hover:scale-105 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]">
                    <Button
                      size="sm"
                      className="relative h-10 overflow-hidden rounded-xl bg-gradient-to-r from-primary to-primary/90 px-6 text-sm font-medium text-primary-foreground shadow-lg transition-all duration-200 hover:scale-105 hover:from-primary hover:to-primary/95 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-95"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTemplateSelected(example);
                      }}
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        <span>Use this template</span>
                        <svg
                          className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                      </span>
                      <span className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent opacity-0 transition-opacity duration-300 hover:opacity-100" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  <span>PDF</span>
                </div>
              </div>

              <h3 className="mt-2 text-base font-semibold leading-tight">{example.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {example.description ?? "A well-structured resume template with a clean layout."}
              </p>
            </div>
          ))}
        </div>

        {filteredExamples.length === 0 && (
          <p className="mt-8 text-center text-muted-foreground">
            No resume templates found for this filter.
          </p>
        )}
      </div>
    </div>
  );
};
